import React, { useState } from "react";
import { Box, Typography, Stack, Chip } from "@mui/material";
import { motion, AnimatePresence } from "framer-motion";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import PlaceIcon from "@mui/icons-material/Place";
import MicIcon from "@mui/icons-material/Mic";
import ForumIcon from "@mui/icons-material/Forum";
import LocalCafeIcon from "@mui/icons-material/LocalCafe";
import RestaurantIcon from "@mui/icons-material/Restaurant";
import CampaignIcon from "@mui/icons-material/Campaign";
import EmojiFlagsIcon from "@mui/icons-material/EmojiFlags";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";

const PRIMARY = "#8B1A1A";
const ACCENT = "#6E1414";

const TIPOS = {
    apertura: { Icon: CampaignIcon, etiqueta: "Apertura" },
    conferencia: { Icon: MicIcon, etiqueta: "Conferencia" },
    panel: { Icon: ForumIcon, etiqueta: "Panel de discusión" },
    cierre: { Icon: EmojiFlagsIcon, etiqueta: "Cierre" },
    refrigerio: { Icon: LocalCafeIcon, etiqueta: "Networking" },
    almuerzo: { Icon: RestaurantIcon, etiqueta: "Almuerzo" },
};

const DIAS = [
    {
        etiqueta: "Día 1",
        fechaCorta: "Jueves 8 Oct",
        fecha: "Jueves 8 de octubre de 2026",
        actividades: [
            { inicio: "08:15", fin: "09:00", tipo: "apertura", titulo: "Palabras de bienvenida" },
            { inicio: "09:00", fin: "09:45", tipo: "conferencia", titulo: "Estabilidad, crecimiento y riesgos del sistema bancario de Guatemala 2026-2027" },
            { inicio: "09:45", fin: "10:15", tipo: "refrigerio", titulo: "Refrigerio, espacio de networking y visita a la sala comercial" },
            { inicio: "10:15", fin: "11:00", tipo: "conferencia", titulo: "Gobierno corporativo y gestión de riesgos: fortaleciendo la relación entre la Junta Directiva, la Alta Gerencia y el CRO" },
            { inicio: "11:00", fin: "11:45", tipo: "conferencia", titulo: "IA y analítica de datos aplicada a decisiones de aprobación de crédito más ágiles y automáticas" },
            { inicio: "11:45", fin: "12:45", tipo: "panel", titulo: "¿Cómo construir resiliencia operativa frente a los riesgos tecnológicos y de ciberseguridad?" },
            { inicio: "12:45", fin: "14:15", tipo: "almuerzo", titulo: "Almuerzo y networking" },
            { inicio: "14:15", fin: "15:00", tipo: "conferencia", titulo: "Open Finance, pagos inmediatos, interoperabilidad, y otras nuevas fuentes de riesgos en la banca" },
            { inicio: "15:00", fin: "15:45", tipo: "conferencia", titulo: "Financiamiento de crédito privado y project finance en América Latina: oportunidades, riesgos y desafíos" },
            { inicio: "15:45", fin: "16:30", tipo: "cierre", etiqueta: "Conferencia de cierre", titulo: "Gestión del riesgo tecnológico (o el costo de reaccionar tarde)" },
            { inicio: "16:30", fin: "17:15", tipo: "refrigerio", titulo: "Refrigerio, espacio de networking y visita a la sala comercial" },
        ],
    },
    {
        etiqueta: "Día 2",
        fechaCorta: "Viernes 9 Oct",
        fecha: "Viernes 9 de octubre de 2026",
        actividades: [
            { inicio: "08:15", fin: "09:15", tipo: "conferencia", subtitulo: "Superintendencia de Bancos", titulo: "Basilea III, ICAAP y supervisión basada en riesgos. ¿Está preparada la banca regional?" },
            { inicio: "09:15", fin: "10:00", tipo: "conferencia", titulo: "Implementación de Basilea III en Latinoamérica y principales retos para los sistemas bancarios" },
            { inicio: "10:00", fin: "10:30", tipo: "refrigerio", titulo: "Refrigerio, espacio de networking y visita a la sala comercial" },
            { inicio: "10:30", fin: "11:15", tipo: "conferencia", titulo: "Gobierno de datos: mejores prácticas y casos de uso en las áreas de administración de riesgos" },
            { inicio: "11:15", fin: "12:00", tipo: "conferencia", titulo: "Fraude digital y autenticación de clientes en la banca del futuro" },
            { inicio: "12:00", fin: "13:00", tipo: "panel", titulo: "Desafíos (y planes de acción) de los CROs bancarios de LatAm" },
            { inicio: "13:00", fin: "14:30", tipo: "almuerzo", titulo: "Almuerzo y networking" },
            { inicio: "14:30", fin: "15:15", tipo: "conferencia", titulo: "Inteligencia Artificial: Gestión integral y gobernanza de riesgos" },
            { inicio: "15:15", fin: "16:00", tipo: "conferencia", titulo: "Pruebas de estrés y analítica de datos aplicados a la transformación en la gestión de riesgos financieros" },
            { inicio: "16:00", fin: "16:30", tipo: "refrigerio", titulo: "Refrigerio, espacio de networking y visita a la sala comercial" },
            { inicio: "16:30", fin: "17:15", tipo: "conferencia", titulo: "Integración del cambio climático en la gestión del riesgo de crédito" },
            { inicio: "17:15", fin: "17:20", tipo: "cierre", etiqueta: "Palabras de cierre", titulo: "Clausura del 11º CLAR 2026 y anuncio de la sede del 12º CLAR 2027" },
        ],
    },
];

const esPausa = (tipo) => tipo === "refrigerio" || tipo === "almuerzo";
const esDestacado = (tipo) => tipo === "apertura" || tipo === "cierre";

function FilaActividad({ actividad, indice }) {
    const meta = TIPOS[actividad.tipo];
    const etiqueta = actividad.etiqueta || meta.etiqueta;
    const colorBorde = actividad.tipo === "panel" ? ACCENT : PRIMARY;

    return (
        <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 + indice * 0.05, duration: 0.35 }}
        >
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "96px 36px 1fr" } }}>
                {/* Hora (solo escritorio) */}
                <Box sx={{ display: { xs: "none", md: "flex" }, flexDirection: "column", alignItems: "flex-end", pt: 2 }}>
                    <Typography sx={{ fontWeight: 900, color: PRIMARY, fontSize: "0.95rem", lineHeight: 1.2 }}>
                        {actividad.inicio}
                    </Typography>
                    <Typography sx={{ color: "#8a8aa3", fontSize: "0.75rem" }}>
                        {actividad.fin}
                    </Typography>
                </Box>

                {/* Riel de la línea de tiempo (solo escritorio) */}
                <Box sx={{ display: { xs: "none", md: "flex" }, justifyContent: "center", position: "relative" }}>
                    <Box sx={{ width: "2px", background: "linear-gradient(180deg, rgba(139,26,26,0.25), rgba(75,63,160,0.25))" }} />
                    <Box sx={{
                        position: "absolute", top: 22, width: 13, height: 13, borderRadius: "50%",
                        background: `linear-gradient(135deg, ${PRIMARY}, ${ACCENT})`,
                        boxShadow: "0 0 0 4px rgba(255,255,255,0.85)",
                    }} />
                </Box>

                {/* Tarjeta */}
                {esPausa(actividad.tipo) ? (
                    <Box sx={{
                        my: 1, px: { xs: 2, md: 3 }, py: 1.5,
                        display: "flex", alignItems: "center", gap: 1.5,
                        borderRadius: "14px",
                        border: "1px dashed rgba(139,26,26,0.3)",
                        backgroundColor: "rgba(255,255,255,0.55)",
                        backdropFilter: "blur(6px)",
                    }}>
                        <meta.Icon sx={{ color: PRIMARY, fontSize: 20, flexShrink: 0 }} />
                        <Typography sx={{ color: "#5a5a78", fontStyle: "italic", fontSize: "0.85rem", lineHeight: 1.4, flex: 1 }}>
                            {actividad.titulo}
                        </Typography>
                        <Typography sx={{ color: "#8a8aa3", fontWeight: 700, fontSize: "0.75rem", flexShrink: 0, display: { xs: "block", md: "none" } }}>
                            {actividad.inicio} – {actividad.fin}
                        </Typography>
                    </Box>
                ) : (
                    <Box sx={{
                        my: 1, p: { xs: 2, md: 2.5 },
                        borderRadius: "16px",
                        ...(esDestacado(actividad.tipo)
                            ? {
                                background: `linear-gradient(135deg, ${PRIMARY} 0%, ${ACCENT} 100%)`,
                                boxShadow: "0 8px 26px rgba(139,26,26,0.28)",
                            }
                            : {
                                backgroundColor: "rgba(255,255,255,0.93)",
                                backdropFilter: "blur(8px)",
                                borderLeft: `4px solid ${colorBorde}`,
                                boxShadow: "0 2px 14px rgba(26,26,46,0.07)",
                            }),
                        transition: "transform 0.2s, box-shadow 0.2s",
                        "&:hover": {
                            transform: "translateY(-3px)",
                            boxShadow: esDestacado(actividad.tipo)
                                ? "0 12px 32px rgba(139,26,26,0.38)"
                                : "0 10px 26px rgba(139,26,26,0.15)",
                        },
                    }}>
                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
                            <Chip
                                icon={<meta.Icon sx={{ fontSize: "15px !important" }} />}
                                label={etiqueta}
                                size="small"
                                sx={{
                                    fontWeight: 800, fontSize: "0.68rem", letterSpacing: 0.5, textTransform: "uppercase", height: 24,
                                    ...(esDestacado(actividad.tipo)
                                        ? { backgroundColor: "rgba(255,255,255,0.18)", color: "white", "& .MuiChip-icon": { color: "white" } }
                                        : { backgroundColor: `${colorBorde}14`, color: colorBorde, "& .MuiChip-icon": { color: colorBorde } }),
                                }}
                            />
                            <Chip
                                label={`${actividad.inicio} – ${actividad.fin}`}
                                size="small"
                                sx={{
                                    fontWeight: 700, fontSize: "0.7rem", height: 24,
                                    display: { xs: "flex", md: "none" },
                                    ...(esDestacado(actividad.tipo)
                                        ? { backgroundColor: "rgba(255,255,255,0.18)", color: "white" }
                                        : { backgroundColor: "rgba(26,26,46,0.06)", color: "#5a5a78" }),
                                }}
                            />
                        </Stack>
                        {actividad.subtitulo && (
                            <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 0.5 }}>
                                <AccountBalanceIcon sx={{ fontSize: 15, color: esDestacado(actividad.tipo) ? "rgba(255,255,255,0.8)" : ACCENT }} />
                                <Typography sx={{ fontWeight: 700, fontSize: "0.75rem", letterSpacing: 0.5, textTransform: "uppercase", color: esDestacado(actividad.tipo) ? "rgba(255,255,255,0.8)" : ACCENT }}>
                                    {actividad.subtitulo}
                                </Typography>
                            </Stack>
                        )}
                        <Typography sx={{
                            fontWeight: 800, lineHeight: 1.35,
                            fontSize: { xs: "0.92rem", md: "1rem" },
                            color: esDestacado(actividad.tipo) ? "white" : "#1a1a2e",
                        }}>
                            {actividad.titulo}
                        </Typography>
                    </Box>
                )}
            </Box>
        </motion.div>
    );
}

export default function AgendaPreliminar() {
    const [diaActivo, setDiaActivo] = useState(0);
    const dia = DIAS[diaActivo];

    return (
        <Box sx={{ width: "100%", maxWidth: "920px", mx: "auto", px: { xs: 2, md: 4 }, pb: 8 }}>
            {/* Encabezado hero */}
            <motion.div
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
            >
                <Box sx={{
                    background: `linear-gradient(135deg, ${PRIMARY} 0%, ${ACCENT} 100%)`,
                    borderRadius: 4,
                    p: { xs: 3, md: 5 },
                    mb: 4,
                    textAlign: "center",
                    position: "relative",
                    overflow: "hidden",
                }}>
                    <Box sx={{ position: "absolute", top: -60, right: -60, width: 200, height: 200, borderRadius: "50%", backgroundColor: "rgba(255,255,255,0.06)" }} />
                    <Box sx={{ position: "absolute", bottom: -50, left: -50, width: 150, height: 150, borderRadius: "50%", backgroundColor: "rgba(255,255,255,0.06)" }} />
                    <Chip
                        label="Programa preliminar · Sujeto a cambios"
                        size="small"
                        sx={{
                            backgroundColor: "rgba(255,255,255,0.16)", color: "white",
                            fontWeight: 700, letterSpacing: 1.5, fontSize: "0.65rem", textTransform: "uppercase", mb: 2,
                        }}
                    />
                    <Typography variant="h4" sx={{ color: "white", fontWeight: 900, lineHeight: 1.15, mb: 1 }}>
                        Agenda Académica
                    </Typography>
                    <Typography sx={{ color: "rgba(255,255,255,0.85)", fontWeight: 700, letterSpacing: 2, textTransform: "uppercase", fontSize: "0.8rem", mb: 2 }}>
                        11º Congreso Latinoamericano de Riesgos – CLAR 2026
                    </Typography>
                    <Box sx={{ width: 60, height: 3, background: "rgba(255,255,255,0.4)", borderRadius: 2, mx: "auto", mb: 2 }} />
                    <Stack direction={{ xs: "column", sm: "row" }} spacing={{ xs: 0.75, sm: 3 }} justifyContent="center" alignItems="center" sx={{ mb: 2 }}>
                        <Stack direction="row" spacing={0.75} alignItems="center">
                            <CalendarMonthIcon sx={{ color: "rgba(255,255,255,0.85)", fontSize: 18 }} />
                            <Typography sx={{ color: "rgba(255,255,255,0.92)", fontSize: "0.85rem", fontWeight: 600 }}>
                                Jueves 8 y viernes 9 de octubre de 2026
                            </Typography>
                        </Stack>
                        <Stack direction="row" spacing={0.75} alignItems="center">
                            <PlaceIcon sx={{ color: "rgba(255,255,255,0.85)", fontSize: 18 }} />
                            <Typography sx={{ color: "rgba(255,255,255,0.92)", fontSize: "0.85rem", fontWeight: 600 }}>
                                Hotel y Museo Casa Santo Domingo, Antigua Guatemala
                            </Typography>
                        </Stack>
                    </Stack>
                    <Typography sx={{ color: "rgba(255,255,255,0.85)", fontStyle: "italic", maxWidth: 500, mx: "auto", lineHeight: 1.6, fontSize: "0.9rem" }}>
                        "Transformando riesgos en resiliencia, rentabilidad y nuevos negocios"
                    </Typography>
                </Box>
            </motion.div>

            {/* Selector de día tipo píldora */}
            <Box sx={{ display: "flex", justifyContent: "center", mb: 4 }}>
                <Box sx={{
                    display: "flex", p: 0.5, borderRadius: "999px",
                    backgroundColor: "rgba(255,255,255,0.9)", backdropFilter: "blur(8px)",
                    border: "1px solid rgba(139,26,26,0.15)",
                    boxShadow: "0 4px 18px rgba(26,26,46,0.08)",
                }}>
                    {DIAS.map((d, i) => (
                        <Box
                            key={i}
                            component="button"
                            onClick={() => setDiaActivo(i)}
                            sx={{
                                position: "relative", border: "none", background: "transparent",
                                borderRadius: "999px", px: { xs: 2.5, md: 4 }, py: 1.25, cursor: "pointer",
                            }}
                        >
                            {diaActivo === i && (
                                <motion.div
                                    layoutId="pildora-dia"
                                    transition={{ type: "spring", stiffness: 400, damping: 32 }}
                                    style={{
                                        position: "absolute", inset: 0, borderRadius: 999,
                                        background: `linear-gradient(135deg, ${PRIMARY}, ${ACCENT})`,
                                        boxShadow: "0 4px 14px rgba(139,26,26,0.3)",
                                    }}
                                />
                            )}
                            <Typography sx={{
                                position: "relative", fontWeight: 800, fontSize: { xs: "0.8rem", md: "0.9rem" },
                                color: diaActivo === i ? "white" : "#5a5a78",
                                transition: "color 0.2s", whiteSpace: "nowrap",
                            }}>
                                {d.etiqueta} · {d.fechaCorta}
                            </Typography>
                        </Box>
                    ))}
                </Box>
            </Box>

            {/* Línea de tiempo del día */}
            <AnimatePresence mode="wait">
                <motion.div
                    key={diaActivo}
                    initial={{ opacity: 0, x: diaActivo === 0 ? -24 : 24 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: diaActivo === 0 ? 24 : -24 }}
                    transition={{ duration: 0.28 }}
                >
                    <Stack direction="row" spacing={1} alignItems="center" justifyContent="center" sx={{ mb: 2.5 }}>
                        <CalendarMonthIcon sx={{ color: PRIMARY, fontSize: 20 }} />
                        <Typography sx={{ fontWeight: 900, color: "#1a1a2e", fontSize: { xs: "1rem", md: "1.15rem" } }}>
                            {dia.fecha}
                        </Typography>
                    </Stack>
                    <Box>
                        {dia.actividades.map((actividad, i) => (
                            <FilaActividad key={`${diaActivo}-${i}`} actividad={actividad} indice={i} />
                        ))}
                    </Box>
                </motion.div>
            </AnimatePresence>

            <Typography sx={{ textAlign: "center", color: "#8a8aa3", fontSize: "0.75rem", mt: 4, fontStyle: "italic" }}>
                Agenda académica preliminar · Los horarios y temas pueden variar
            </Typography>
        </Box>
    );
}
